import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Button, Modal } from './ui';
import { useT } from '../i18n/locale';

export type ConfirmTone = 'danger' | 'warn' | 'info';

export interface ConfirmOptions {
  title: string;
  /** The main sentence. Say what will happen, not "are you sure?". */
  body: ReactNode;
  /** Facts worth restating before an irreversible action. */
  details?: Array<{ label: string; value: string }>;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Omit the cancel button to make this an acknowledgement, not a question. */
  acknowledgeOnly?: boolean;
}

type Resolver = (ok: boolean) => void;

const ConfirmContext = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

/**
 * Promise-based confirmation, so call sites read like window.confirm:
 *
 *   if (!(await confirm({ title, body }))) return;
 *
 * window.confirm was doing this job, which meant OS-styled dialogs that
 * ignore the app's language and spacing, cannot show structured detail, and
 * block the whole tab while open. This keeps the one-line ergonomics and
 * renders in the app's own vocabulary.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  /*
   * Focus the SAFE option on a destructive prompt: someone hammering Enter
   * should cancel, not delete. Harmless prompts focus the confirm button so
   * the keyboard path stays quick.
   */
  useEffect(() => {
    if (!options) return;
    const target =
      options.tone === 'danger' && !options.acknowledgeOnly
        ? cancelButtonRef.current
        : confirmButtonRef.current;
    const id = window.setTimeout(() => target?.focus(), 40);
    return () => window.clearTimeout(id);
  }, [options]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      <Modal
        open={options !== null}
        title={options?.title ?? ''}
        onClose={() => settle(false)}
        width="max-w-md"
        footer={
          <>
            {options?.acknowledgeOnly ? null : (
              <Button ref={cancelButtonRef} variant="secondary" onClick={() => settle(false)}>
                {options?.cancelLabel ?? t('common.cancel')}
              </Button>
            )}
            <Button
              ref={confirmButtonRef}
              variant={options?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ??
                (options?.acknowledgeOnly ? t('confirm.acknowledge') : t('confirm.continue'))}
            </Button>
          </>
        }
      >
        {options?.tone === 'danger' || options?.tone === 'warn' ? (
          <Alert tone={options.tone === 'danger' ? 'error' : 'warn'}>{options.body}</Alert>
        ) : (
          <p className="m-0 text-sm text-slate-600">{options?.body}</p>
        )}

        {options?.details?.length ? (
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-slate-100 pt-4 text-sm">
            {options.details.map((d) => (
              <div key={d.label}>
                <dt className="text-xs font-bold text-slate-500">{d.label}</dt>
                <dd className="m-0 font-bold text-slate-800">{d.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (o: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}
